import { defineStore } from 'pinia'
import {
  POLICY_IDS,
  canAccessDashboardPathByContext,
  canAccessMenuByContext,
  resolvePolicyIds,
  resolveRoleId,
  type MenuKey,
} from '~/utils/roleAccess'
import { RESCUE_CENTER_LABELS, isKnownRescueCenter, normalizeRescueCenters } from '~~/shared/rescue-centers'

const initialUser = {
  id: null,
  first_name: null,
  last_name: null,
  email: null,
  password: null,
  location: null,
  title: null,
  description: null,
  tags: null,
  avatar: null,
  language: null,
  tfa_secret: null,
  status: null,
  role: null,
  token: null,
  last_access: null,
  last_page: null,
  provider: null,
  external_identifier: null,
  auth_data: null,
  email_notifications: null,
  appearance: null,
  theme_dark: null,
  theme_light: null,
  theme_light_overrides: null,
  theme_dark_overrides: null,
  points: null,
  rescue_center: 1337,
  rescue_centers: [] as number[],
  policies: [] as string[],
}

export const state = () => ({
  user: { ...initialUser },
  // Statut 2FA : `required` = imposée par une policy de l'utilisateur, `enabled` = déjà configurée
  tfa: { enabled: false, required: false },
})

export const useUserStore = defineStore('userStore', {
  persist: true,
  state,
  getters: {
    // États de l'utilisateur
    isLoggedIn: state => !!state.user.id,

    // La 2FA est obligatoire pour cet utilisateur mais pas encore configurée
    needsTfaSetup: state => Boolean(state.user.id) && state.tfa.required && !state.tfa.enabled,

    // Objet utilisateur complet
    getUser: state => state.user,

    // Propriétés individuelles pour destructuration
    userId: state => state.user.id,
    firstName: state => state.user.first_name,
    lastName: state => state.user.last_name,
    fullName: state => state.user.first_name && state.user.last_name
      ? `${state.user.first_name} ${state.user.last_name}`
      : null,
    email: state => state.user.email,
    avatar: state => state.user.avatar,
    role: state => state.user.role,
    policyIds: state => resolvePolicyIds(state.user),
    hasCaPolicy: (state) => {
      const policyIds = resolvePolicyIds(state.user)
      return policyIds.includes(POLICY_IDS.CA_MEMBER)
        || policyIds.includes(POLICY_IDS.SERVICE_MANAGER)
    },
    token: state => state.user.token ?? 'non',
    points: state => state.user.points,
    rescueCenterId: state => state.user.rescue_center,

    // Centre de secours formaté
    rescueCenter: (state) => {
      switch (state.user.rescue_center) {
        case 0:
          return 'Direction Nationale'
        case 2:
          return 'CS Luxembourg'
        case 3:
          return 'Equipe Médicale'
        case 7:
          return 'CS Hainaut - Namur'
        case 9:
          return 'CS Liège'
        case 10:
          return 'Département Formation'
        default:
          return 'Non défini.'
      }
    },

    // Tous les centres du volontaire (centre par défaut en premier) : { id, label, isDefault }
    rescueCenters: (state) => {
      const defaultCenter = state.user.rescue_center
      return normalizeRescueCenters(state.user.rescue_centers, isKnownRescueCenter(defaultCenter) ? defaultCenter : null)
        .map(id => ({ id, label: RESCUE_CENTER_LABELS[id] as string, isDefault: id === defaultCenter }))
    },

    // URL de l'avatar
    avatarUrl: (state) => {
      if (state.user?.avatar) {
        return `https://api.ambulancepresent.be/assets/${state.user.avatar}`
      }
      else {
        const seed = state.user?.id || state.user?.email || 'default'
        return `https://api.dicebear.com/9.x/adventurer-neutral/svg?seed=${seed}&backgroundType=gradientLinear&backgroundRotation=-320&backgroundColor=b6e3f4,c0aede`
      }
    },

    getUrlAvatar: (state) => {
      if (state.user?.avatar) {
        return `https://api.ambulancepresent.be/assets/${state.user.avatar}`
      }
      else {
        const seed = state.user?.id || state.user?.email || 'default'
        return `https://api.dicebear.com/9.x/adventurer-neutral/svg?seed=${seed}&backgroundType=gradientLinear&backgroundRotation=-320&backgroundColor=b6e3f4,c0aede`
      }
    },
  },
  actions: {
    setUserLocal(user: any) {
      const normalizedPolicies = resolvePolicyIds(user)
      this.user = user
      this.user.policies = normalizedPolicies
    },
    setTfaStatus(status?: { enabled?: boolean, required?: boolean } | null) {
      this.tfa = { enabled: Boolean(status?.enabled), required: Boolean(status?.required) }
    },
    canAccessMenu(menuKey: MenuKey) {
      const roleId = resolveRoleId(this.user.role)
      const policyIds = resolvePolicyIds(this.user)
      return canAccessMenuByContext(roleId, policyIds, menuKey)
    },
    canAccessPath(path: string) {
      const roleId = resolveRoleId(this.user.role)
      const policyIds = resolvePolicyIds(this.user)
      return canAccessDashboardPathByContext(roleId, policyIds, path)
    },
    logout() {
      this.user = { ...initialUser }
      this.tfa = { enabled: false, required: false }
      clearOfflineCache()
    },
  },
})
